import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Loader, Tag } from "@threadws/jute";
import type { Agent, UserWithPersonas } from "../../api/generated";
import { agentsActivateMutation, agentsVersionsOptions } from "../../api/generated/@tanstack/react-query.gen";
import { formatDate } from "../../utils/formatDate";
import { userName } from "../../utils/userName";

interface VersionHistoryProps {
  agent: Agent;
  users: UserWithPersonas[];
  /** Called with the agent once another version is active, so the page can refresh. */
  onActivated: (agent: Agent) => Promise<unknown>;
}

/** Every version of the agent's prompt, newest first. Make an older one active to roll back. */
export function VersionHistory({ agent, users, onActivated }: VersionHistoryProps) {
  const { data: versions, error } = useQuery(agentsVersionsOptions({ path: { id: agent.id } }));
  const activate = useMutation({ ...agentsActivateMutation(), onSuccess: onActivated });
  const author = (id: string | null) => {
    const user = users.find((user) => user.id === id);
    return user ? userName(user) : "Sage's default";
  };

  if (error) return <p className="text-sm text-danger">Couldn't load versions: {error.message}</p>;
  if (!versions) return <Loader label="Loading versions" />;

  return (
    <ol className="flex flex-col">
      {activate.error && <p className="text-sm text-danger">{activate.error.message}</p>}
      {versions.map((version) => {
        const active = version.id === agent.activeVersion.id;
        return (
          <li key={version.id} className="flex flex-col gap-2 border-b border-border-subtle py-3 last:border-b-0">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-mono font-medium">v{version.version}</span>
              {active && <Tag tone="success">Active</Tag>}
              <span className="font-mono text-xs text-text-secondary">{version.model}</span>
              <span className="text-xs text-text-secondary">
                {author(version.createdBy)} · {formatDate(version.createdAt)}
              </span>
              {!active && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon=""
                  className="ml-auto"
                  disabled={activate.isPending}
                  onClick={() => activate.mutate({ path: { id: agent.id, versionId: version.id } })}
                >
                  Make active
                </Button>
              )}
            </div>
            {version.note && <p className="text-sm">{version.note}</p>}
            <details className="text-sm">
              <summary className="cursor-pointer text-text-secondary">Prompt</summary>
              <pre className="mt-2 max-h-96 overflow-auto rounded-md bg-surface-card p-3 font-mono text-xs whitespace-pre-wrap">
                {version.systemPrompt}
              </pre>
            </details>
          </li>
        );
      })}
    </ol>
  );
}
